import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import './WhatsAppSupport.css';

export default function WhatsAppSupport({
  open,
  setOpen,
  defaultVersion = 'v18.0',
  defaultPhoneNumberId = 'YOUR_PHONE_NUMBER_ID',
  defaultAccessToken = 'YOUR_ACCESS_TOKEN',
  defaultSupportNumber = 'YOUR_SUPPORT_NUMBER'
}) {
  const [activeTab, setActiveTab] = useState('chat');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [supportNumber, setSupportNumber] = useState('');
  const [apiVersion, setApiVersion] = useState('v18.0');
  const [testSending, setTestSending] = useState(false);
  const panelRef = useRef(null);

  // Load configuration from localStorage on mount, fall back to ETPOSDashboard defaults
  useEffect(() => {
    const storedPhoneId = localStorage.getItem('whatsapp_phone_number_id') || defaultPhoneNumberId;
    const storedToken = localStorage.getItem('whatsapp_access_token') || defaultAccessToken;
    const storedSupportNum = localStorage.getItem('support_whatsapp_number') || defaultSupportNumber;
    const storedVersion = localStorage.getItem('whatsapp_api_version') || defaultVersion;

    setPhoneNumberId(storedPhoneId);
    setAccessToken(storedToken);
    setSupportNumber(storedSupportNum);
    setApiVersion(storedVersion);
  }, [defaultVersion, defaultPhoneNumberId, defaultAccessToken, defaultSupportNumber]);

  // Save config to localStorage
  const handleSaveSettings = (e) => {
    e.preventDefault();
    localStorage.setItem('whatsapp_phone_number_id', phoneNumberId);
    localStorage.setItem('whatsapp_access_token', accessToken);
    localStorage.setItem('support_whatsapp_number', supportNumber);
    localStorage.setItem('whatsapp_api_version', apiVersion);
    toast.success('WhatsApp configurations saved successfully!');
    setActiveTab('chat');
  };

  // Open Direct WhatsApp Chat Link
  const handleDirectChat = () => {
    const cleanNum = supportNumber.replace(/\D/g, ''); // strip non-numeric characters
    if (!cleanNum || supportNumber === 'YOUR_SUPPORT_NUMBER') {
      toast.info('Support phone number is not configured in settings.');
      setActiveTab('settings');
      return;
    }
    const url = `https://wa.me/${cleanNum}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Send Test Support Message
  const handleSendTestMessage = async () => {
    if (phoneNumberId === 'YOUR_PHONE_NUMBER_ID' || accessToken === 'YOUR_ACCESS_TOKEN') {
      toast.info('Please update WhatsApp Phone Number ID and Access Token in the Settings tab.');
      setActiveTab('settings');
      return;
    }
    if (supportNumber === 'YOUR_SUPPORT_NUMBER' || !supportNumber) {
      toast.info('Please configure the recipient Support Number in the Settings tab.');
      setActiveTab('settings');
      return;
    }

    setTestSending(true);
    try {
      const url = `https://graph.facebook.com/${apiVersion}/${phoneNumberId}/messages`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messaging_product: 'whatsapp',
          to: supportNumber.replace(/\D/g, ''),
          type: 'template',
          template: {
            name: 'hello_world',
            language: { code: 'en_US' },
          },
        }),
      });
      const data = await response.json();
      if (response.ok && !data.error) {
        toast.success('Test support message sent successfully via WhatsApp!');
      } else {
        toast.error('WhatsApp API error: ' + (data.error?.message || JSON.stringify(data)));
      }
    } catch (err) {
      toast.error('Failed to send test message: ' + err.message);
    } finally {
      setTestSending(false);
    }
  };

  return (
    <>
      {/* Floating Toggle Button */}
      <button
        className="whatsapp-float-btn"
        onClick={() => setOpen(prev => !prev)}
        title="WhatsApp Support"
        aria-label="Open WhatsApp Support"
      >
        <span className="whatsapp-float-pulse" />
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <i className="fa-brands fa-whatsapp" style={{ fontSize: '26px' }}></i>
        )}
      </button>

      {/* Support Panel */}
      {open && (
        <div className="whatsapp-support-overlay" ref={panelRef}>
          {/* Header */}
          <div className="whatsapp-support-header">
            <div className="whatsapp-header-icon-wrapper">
              <i className="fa-brands fa-whatsapp"></i>
            </div>
            <div className="whatsapp-header-info">
              <div className="whatsapp-header-title">WhatsApp Support</div>
              <div className="whatsapp-header-subtitle">Chat or manage configurations</div>
            </div>
            <button className="whatsapp-header-close" onClick={() => setOpen(false)} aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="whatsapp-tabs">
            <button
              className={`whatsapp-tab-btn ${activeTab === 'chat' ? 'active' : ''}`}
              onClick={() => setActiveTab('chat')}
            >
              💬 Support
            </button>
            <button
              className={`whatsapp-tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              ⚙️ Settings
            </button>
          </div>

          {/* Tab: Support Chat */}
          {activeTab === 'chat' && (
            <div className="whatsapp-tab-content">
              <div className="whatsapp-chat-card">
                <div className="support-badge">Official Channel</div>
                <h3>Connect Directly</h3>
                <p>Chat directly with our support team on WhatsApp for fast assistance, troubleshooting, or general questions.</p>
                <button className="whatsapp-action-chat-btn" onClick={handleDirectChat}>
                  <i className="fa-brands fa-whatsapp" style={{ marginRight: '8px', fontSize: '18px' }}></i>
                  Start WhatsApp Chat
                </button>
              </div>

              <div className="whatsapp-test-card">
                <h3>Test API Configuration</h3>
                <p>Send a standard "Hello World" test template notification message to your support number to verify your API credentials.</p>
                <button
                  className="whatsapp-action-test-btn"
                  onClick={handleSendTestMessage}
                  disabled={testSending}
                >
                  {testSending ? 'Sending...' : '📨 Send Test Notification'}
                </button>
              </div>
            </div>
          )}

          {/* Tab: API Configuration */}
          {activeTab === 'settings' && (
            <form className="whatsapp-tab-content whatsapp-settings-form" onSubmit={handleSaveSettings}>
              <h3>API Configuration</h3>
              
              <div className="whatsapp-form-group">
                <label>Phone Number ID</label>
                <input
                  type="text"
                  placeholder="Enter Phone Number ID"
                  value={phoneNumberId}
                  onChange={(e) => setPhoneNumberId(e.target.value)}
                  required
                />
              </div>

              <div className="whatsapp-form-group">
                <label>Access Token</label>
                <input
                  type="password"
                  placeholder="Enter Access Token"
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                  required
                />
              </div>

              <div className="whatsapp-form-group">
                <label>Support Phone Number</label>
                <input
                  type="text"
                  placeholder="e.g. 15550199999"
                  value={supportNumber}
                  onChange={(e) => setSupportNumber(e.target.value)}
                  required
                />
                <span className="help-text">Please include country code without "+" (e.g., 15550199999)</span>
              </div>

              <div className="whatsapp-form-group">
                <label>API Version</label>
                <input
                  type="text"
                  placeholder="e.g. v18.0"
                  value={apiVersion}
                  onChange={(e) => setApiVersion(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="whatsapp-save-btn">
                💾 Save Configurations
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
}
